import axios from 'axios';

import config from '../../../config';
import { GargiOpportunity, InteractionsResponse, OpportunitiesResponse, OpportunityCategory, TicketGroup } from '../interfaces/gargi';
import { DesignedJourney } from '../interfaces/models';

const { apiServerUrl } = config;

// The same endpoints and payloads the Gargi app uses; business and tenant come from the signed-in user.

export const getOpportunityCategories = () =>
  axios.get<OpportunityCategory[]>(`${apiServerUrl}/users/opportunity/categories`).then(({ data }) => data);

export const getOpportunities = (categoryId: number) =>
  axios.get<OpportunitiesResponse>(`${apiServerUrl}/users/opportunity/categories/${categoryId}`).then(({ data }) => data);

export const getTicketGroups = () =>
  axios.get<TicketGroup[]>(`${apiServerUrl}/users/ticket-desk/config/group`).then(({ data }) => data.filter((g) => g.active));

const body = (o: GargiOpportunity, notes?: string) => ({
  cohortId: o.id,
  metadata: o.metadata ?? {},
  customerCount: o.customerCount ?? null,
  predictedRevenue: o.predictedRevenue ?? null,
  atv: o.atv ?? null,
  ...(notes ? { notes } : {}),
});

// Creates a Ticket Desk ticket for the group (with a "Create journey" link) and records the assignment.
export const assignOpportunity = (o: GargiOpportunity, ticketGroupId: number, priority: 'low' | 'medium' | 'high', notes?: string) =>
  axios.post<{ id: string }>(`${apiServerUrl}/users/opportunity/assignment`, { ...body(o, notes), ticketGroupId, priority }).then(({ data }) => data);

export const ignoreOpportunity = (o: GargiOpportunity, notes?: string) =>
  axios.post(`${apiServerUrl}/users/opportunity/ignore`, body(o, notes)).then(({ data }) => data);

export const reconsiderOpportunity = (interactionId: string) =>
  axios.delete(`${apiServerUrl}/users/opportunity/ignore/${interactionId}`).then(({ data }) => data);

// Moves assigned work between Growth Desk's lanes (open = Assigned, in_progress = Journey in draft, closed = Live) or
// closes it as not being done (rejected).
export type AssignmentMove = 'open' | 'in_progress' | 'closed' | 'rejected';
export const updateAssignmentStatus = (id: string, status: AssignmentMove) =>
  axios.put<{ id: string; status: AssignmentMove }>(`${apiServerUrl}/users/opportunity/interactions/${id}/status`, { status }).then(({ data }) => data);

export const getInteractions = (type: 'assigned' | 'ignored', page = 0, limit = 20) =>
  axios.get<InteractionsResponse>(`${apiServerUrl}/users/opportunity/interactions`, { params: { type, page, limit } }).then(({ data }) => data);

// Records the opportunity as taken up by the signed-in user (no ticket) and drafts its journey with a control group.
export interface JourneyPlan {
  channel: string;
  message: string;
}

// request: the question that led to it, so the journey builder's Gargi panel opens on the same conversation.
export const launchOpportunity = (o: GargiOpportunity, plan?: JourneyPlan, request?: string) =>
  axios.post<{ id: string; url: string }>(`${apiServerUrl}/users/opportunity/launch`, { ...body(o), campaign: o.campaign ?? null, ...(plan ? { plan } : {}), ...(request ? { request: request.slice(0, 500) } : {}) })
    .then(({ data }) => data);

// Saves the opportunity's customers as a cohort of this business, usable in any journey.
export const buildAudience = (o: GargiOpportunity, name?: string) =>
  axios.post<{ cohortId: string; name: string }>(`${apiServerUrl}/users/opportunity/audience`, { ...body(o), ...(name ? { name } : {}) })
    .then(({ data }) => data);

export interface ScreenFact {
  label: string;
  current: number;
  previous?: number | null;
  format: 'inr' | 'count' | 'percent';
}

export type ScreenName = 'overview' | 'customer-analysis' | 'store-analysis' | 'product-analysis' | 'festival-analysis' | 'campaign-reports' | 'executive-summary' | 'abandonment' | 'customer-360';

export interface ScreenSummaryRequest {
  screen: ScreenName;
  title: string;
  period: string;
  facts: ScreenFact[];
}

// Plain-words summary of the numbers an existing screen shows; `summary` is null when there is none to show.
export const getScreenSummary = (request: ScreenSummaryRequest) =>
  axios.post<{ summary: string | null; action?: string }>(`${apiServerUrl}/users/intelligence/summary`, request).then(({ data }) => data);

// Live audience size for a template and card values (e.g. a screen's date range).
export const previewOpportunity = (o: GargiOpportunity) =>
  axios.post<{ customerCount: number; atv: number | null }>(`${apiServerUrl}/users/opportunity/preview`, { cohortId: o.id, metadata: o.metadata ?? {} })
    .then(({ data }) => data);

// A journey draft for a plan with no ready-made audience; the builder opens it and the person picks the audience.
export const draftFromBrief = (title: string, brief: string, plan?: JourneyPlan, request?: string) =>
  axios.post<{ id: string; url: string }>(`${apiServerUrl}/users/opportunity/brief-draft`, { title, brief, ...(plan ? { plan } : {}), ...(request ? { request: request.slice(0, 500) } : {}) })
    .then(({ data }) => data);

export interface ExploredAudience {
  id: string;
  name: string;
  description: string;
  query: string;
  customers: number;
  sample: { mobile: string; last_visit: string; visits: number; spend: number }[];
}

export const exploreAudience = (cohortId: string, metadata: Record<string, string | number> = {}) =>
  axios.post<ExploredAudience>(`${apiServerUrl}/users/opportunity/explore`, { cohortId, metadata }).then(({ data }) => data);

// Downloads the full audience as CSV (full mobiles); the server logs who downloaded it.
export const downloadAudience = async (cohortId: string, metadata: Record<string, string | number>, name: string) => {
  const { data } = await axios.post<Blob>(`${apiServerUrl}/users/opportunity/export`, { cohortId, metadata }, { responseType: 'blob', timeout: 10 * 60_000 });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${name.replace(/[^\w-]+/g, '-').replace(/^-|-$/g, '') || 'audience'}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Card values travel in the page link so a Gargi card opens exactly its audience.
export const audiencePath = (cohortId: string, metadata: Record<string, string | number> = {}) =>
  `/audiences/${cohortId}${Object.keys(metadata).length ? `?values=${encodeURIComponent(JSON.stringify(metadata))}` : ''}`;

// Saves an audience Gargi defined in chat as one of this business's cohorts (the query never leaves the servers).
export const saveGargiAudience = (id: string, name?: string) =>
  axios.post<{ cohortId: string; name: string; size: number; created: boolean }>(`${apiServerUrl}/users/intelligence/audiences/${id}`, name ? { name } : {})
    .then(({ data }) => data);

// Creates the builder draft for a journey Gargi designed; api-server re-checks every template, offer and audience.
export const createDesignedJourney = (journey: DesignedJourney, request?: string) =>
  axios.post<{ id: string; url: string }>(`${apiServerUrl}/users/opportunity/journey`, {
    name: journey.name,
    summary: journey.summary,
    audience: {
      play_template_id: journey.audience.play_template_id, audience_id: journey.audience.audience_id || null,
      cohort_id: journey.audience.cohort_id || null, description: journey.audience.description,
    },
    control_percent: journey.control_percent,
    steps: journey.steps,
    ...(request ? { request: request.slice(0, 500) } : {}),
  }).then(({ data }) => data);
